import React, { useState, useEffect } from 'react';
import apiFetch from '@wordpress/api-fetch';
import { useToast } from '../../utils/Toast.jsx';
import { renderInputRow } from '../../utils/renderInputRow.js';

const GeneralSettingsTab = () => {
  const toast = useToast();
  const [originalSettings, setOriginalSettings] = useState({});
  const [Settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const global = window.DOTFW_GLOBAL_OPTIONS || {};
    setSettings(global);
    setOriginalSettings(global);
  }, []);

  const handleSave = () => {
    const changedData = Object.fromEntries(
      Object.entries(Settings).filter(([key, value]) => originalSettings[key] !== value)
    );

    if (Object.keys(changedData).length === 0) {
      toast.info('No changes detected.');
      return;
    }

    setLoading(true);
    setSaved(false);

    apiFetch({ path: '/wp/v2/settings', method: 'POST', data: changedData })
      .then(() => {
        toast.success('Settings saved successfully!');
        window.DOTFW_GLOBAL_OPTIONS = { ...(window.DOTFW_GLOBAL_OPTIONS || {}), ...Settings };
        setOriginalSettings(Settings);
        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
      })
      .catch((err) => {
        console.error('Error saving general settings:', err);
        toast.error('Failed to save settings. Please try again.');
      })
      .finally(() => setLoading(false));
  };

  const updateSettings = (key, value) =>
    setSettings((prev) => ({ ...prev, [key]: value }));

  const isPredefined = Settings.dotfw_select_donation_types === 'predefined_value';

  return (
    <div className="tab-root">

      {/* ── Loading overlay ─────────────────────────────────── */}
      {loading && (
        <div className="spinner-overlay" aria-live="polite" aria-label="Saving">
          <div className="spinner-overlay-card">
            <span className="rotating-spinner" aria-hidden="true" />
            <span className="spinner-overlay-text">Saving changes…</span>
          </div>
        </div>
      )}

      {/* ── Section: General ────────────────────────────────── */}
      <div className="tab-section">
        <div className="tab-section-header">
          <div className="tab-section-meta">
            <h3 className="tab-section-title">General Settings</h3>
            <p className="tab-section-sub">Core plugin configuration</p>
          </div>
        </div>

        <div className="tab-section-body">
          {renderInputRow(
            'Enable Donation / Tip',
            'dotfw_enable_disable_dot',
            'checkBox', [],
            'Enable donation & tip functionality',
            Settings, updateSettings
          )}
          {renderInputRow(
            'Field title',
            'dotfw_dot_main_title',
            'text', [], '',
            Settings, updateSettings, '', true
          )}
          {renderInputRow(
            'Donation type',
            'dotfw_select_donation_types',
            'select',
            [
              { label: 'Percentage (%) / Fixed of predefined values', value: 'predefined_value' },
              { label: 'Only donations text field', value: 'donation_text_value' },
            ],
            '', Settings, updateSettings
          )}

          {/* Conditional predefined fields */}
          {isPredefined && (
            <div className="gst-conditional-block">
              <div className="gst-conditional-label">
                <span className="gst-conditional-icon" aria-hidden="true">↳</span>
                Predefined value options
              </div>
              {renderInputRow(
                'Currency symbol',
                'dotfw_dot_currency_symbol',
                'text', [], '',
                Settings, updateSettings
              )}
              {renderInputRow(
                'Predefined amount type',
                'dotfw_select_amount_types',
                'select',
                [
                  { label: 'Percentage (%) amount', value: 'dot_percentage' },
                  { label: 'Fixed amount', value: 'dot_fixed' },
                ],
                '', Settings, updateSettings
              )}
              {renderInputRow(
                'Predefined values',
                'dotfw_predefined_dotvalue',
                'textArea', [], '',
                Settings, updateSettings,
                'Enter comma-separated values, e.g. 5, 10, 15, 20'
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Section: Display ────────────────────────────────── */}
      <div className="tab-section">
        <div className="tab-section-header">
          <div className="tab-section-meta">
            <h3 className="tab-section-title">Display Settings</h3>
            <p className="tab-section-sub">Control where the field appears</p>
          </div>
        </div>

        <div className="tab-section-body">
          {renderInputRow(
            'Show on cart page',
            'dotfw_display_on_cart_page',
            'checkBox', [],
            'Display donation field on the cart page',
            Settings, updateSettings
          )}
          {renderInputRow(
            'Show on checkout page',
            'dotfw_display_on_checkout_page',
            'checkBox', [],
            'Display donation field on the checkout page',
            Settings, updateSettings
          )}
          {renderInputRow(
            '"Other" option',
            'dotfw_display_other_option',
            'checkBox', [],
            'Show an "Other" option alongside predefined values',
            Settings, updateSettings
          )}
          {renderInputRow(
            'Show "Remove donation" button',
            'dotfw_display_remove_btn',
            'checkBox', [],
            'Allow customers to remove their donation',
            Settings, updateSettings
          )}
        </div>
      </div>

      {/* ── Section: Position ───────────────────────────────── */}
      <div className="tab-section">
        <div className="tab-section-header">
          <div className="tab-section-meta">
            <h3 className="tab-section-title">Position Settings</h3>
            <p className="tab-section-sub">Choose where the field is placed in the layout</p>
          </div>
        </div>

        <div className="tab-section-body">
          {renderInputRow(
            'Cart page position',
            'dotfw_position_cart_page',
            'select',
            [
              { label: 'Before WooCommerce cart',       value: 'woo_before_cart' },
              { label: 'WooCommerce cart collaterals',  value: 'woo_cart_collaterals' },
              { label: 'After WooCommerce cart',        value: 'woo_after_cart' },
            ],
            '', Settings, updateSettings
          )}
          {renderInputRow(
            'Checkout page position',
            'dotfw_position_checkout_page',
            'select',
            [
              { label: 'Before WooCommerce checkout form',        value: 'woo_before_checkout_form' },
              { label: 'After WooCommerce checkout order review', value: 'woo_checkout_after_order_reciew' },
              { label: 'After WooCommerce checkout billing form', value: 'woo_after_checkout_billing_form' },
              { label: 'After WooCommerce checkout form',         value: 'woo_after_chekcout_form' },
            ],
            '', Settings, updateSettings
          )}
        </div>
      </div>

      {/* ── Save row ────────────────────────────────────────── */}
      <div className="tab-save-row">
        <div className="tab-save-row-left">
          {saved && (
            <span className="tab-save-badge" aria-live="polite">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <circle cx="7" cy="7" r="6.5" fill="#d1fae5" stroke="#34d399" />
                <path d="M4 7l2 2 4-4" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              All changes saved
            </span>
          )}
        </div>

        <button
          className={`tab-save-btn${loading ? ' tab-save-btn--busy' : ''}`}
          onClick={handleSave}
          disabled={loading}
          type="button"
        >
          {loading ? (
            <>
              <span className="tab-btn-spinner" aria-hidden="true" />
              Saving…
            </>
          ) : (
            <>
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
                <path d="M2 7.5L6 11.5L13 4" stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Save Changes
            </>
          )}
        </button>
      </div>

    </div>
  );
};

export default GeneralSettingsTab;